<template>
  <div>
    <div v-if="!authReady" class="text-center py-16">
      <v-progress-circular
        indeterminate
        color="primary"
      ></v-progress-circular>
    </div>

    <login-first v-else-if="!uid" />

    <div v-else-if="!conventionLoaded" class="text-center py-16">
      <v-progress-circular
        indeterminate
        color="primary"
      ></v-progress-circular>
    </div>

    <v-container v-else-if="!convention">
      <v-card outlined class="rounded-lg text-center pa-8">
        <v-icon size="56" color="grey lighten-1" v-text="'mdi-notebook-remove-outline'"></v-icon>
        <div class="text-h6 mt-3">Convention not found</div>
        <v-btn
          class="mt-3"
          color="primary"
          text
          to="/convention"
          v-text="'Back to conventions'"
        ></v-btn>
      </v-card>
    </v-container>

    <v-container v-else-if="!isMember">
      <v-card outlined class="rounded-lg text-center pa-8">
        <v-icon size="56" color="primary" v-text="'mdi-account-lock-outline'"></v-icon>
        <div class="text-h6 mt-3">You are not a member yet</div>
        <v-btn
          class="mt-3"
          color="primary"
          text
          to="/convention"
          v-text="'Back to conventions'"
        ></v-btn>
      </v-card>
    </v-container>

    <v-container v-else>
      <v-card outlined class="rounded-lg">
        <v-toolbar flat color="white">
          <v-btn
            icon
            :to="'/convention/' + id"
            aria-label="Back to the contract tree"
          >
            <v-icon v-text="'mdi-arrow-left'"></v-icon>
          </v-btn>
          <v-toolbar-title class="font-weight-bold" v-text="'Followers'"></v-toolbar-title>
        </v-toolbar>

        <v-divider></v-divider>

        <v-card-subtitle class="pb-1" v-text="convention.name"></v-card-subtitle>
        <v-card-subtitle class="pt-0 caption grey--text text--darken-1">
          Admins can tick contracts and promote other members. The person who
          created the convention always keeps admin rights.
        </v-card-subtitle>

        <v-progress-linear
          v-if="loadingMembers"
          indeterminate
          color="primary"
        ></v-progress-linear>

        <v-list v-else>
          <v-list-item v-for="member in members" :key="member.uid">
            <v-list-item-avatar color="#E3F2F1">
              <span class="primary--text font-weight-bold" v-text="initials(member)"></span>
            </v-list-item-avatar>

            <v-list-item-content>
              <v-list-item-title>
                {{ member.name }}
                <span
                  v-if="member.username"
                  class="caption grey--text"
                  v-text="'(' + member.username + ')'"
                ></span>
              </v-list-item-title>
              <v-list-item-subtitle v-if="member.isOwner" v-text="'Created this convention'"></v-list-item-subtitle>
            </v-list-item-content>

            <v-chip
              v-if="member.isAdmin"
              x-small
              label
              color="primary"
              text-color="white"
              v-text="'Admin'"
            ></v-chip>

            <div v-if="canManage(member)" @click.stop>
              <v-menu offset-y left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    aria-label="Member options"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon small v-text="'mdi-dots-vertical'"></v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item @click="setAdmin(member)">
                    <v-list-item-icon>
                      <v-icon small v-text="'mdi-shield-account-outline'"></v-icon>
                    </v-list-item-icon>
                    <v-list-item-title
                      v-text="
                        member.isAdmin
                          ? 'Dismiss as admin'
                          : 'Make convention admin'
                      "
                    ></v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </v-list-item>

          <v-list-item v-if="members.length === 0">
            <v-list-item-content>
              <v-list-item-subtitle v-text="'No member has joined this convention yet.'"></v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card>
    </v-container>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      timeout="4000"
      v-text="snackbar.text"
    ></v-snackbar>
  </div>
</template>

<script>
import { db } from "@/App.vue";
import { ref, onValue, get, set } from "firebase/database";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import LoginFirst from "./LoginFirst.vue";

export default {
  name: "ConventionFollowersVue",

  components: { LoginFirst },

  data: () => ({
    id: "",
    uid: null,
    authReady: false,
    connected: true,
    convention: null,
    conventionLoaded: false,
    members: [],
    loadingMembers: false,
    membersToken: 0,
    saving: false,
    stopAuth: null,
    stopConnection: null,
    stopConvention: null,
    snackbar: { show: false, text: "", color: "success" },
  }),

  computed: {
    isMember() {
      if (!this.convention || !this.uid) return false;
      if (this.convention.owner === this.uid) return true;
      return !!(this.convention.follower && this.convention.follower[this.uid]);
    },

    isAdmin() {
      if (!this.convention || !this.uid) return false;
      if (this.convention.owner === this.uid) return true;
      return !!(
        this.convention.follower && this.convention.follower[this.uid] === "admin"
      );
    },
  },

  created() {
    this.id = this.$route.params.id;

    this.stopAuth = onAuthStateChanged(getAuth(), (user) => {
      this.uid = user ? user.uid : null;
      this.authReady = true;
    });

    this.stopConnection = onValue(
      ref(db, ".info/connected"),
      (snapshot) => {
        this.connected = snapshot.val() === true;
      }
    );

    this.stopConvention = onValue(
      ref(db, "conventions/" + this.id),
      (snapshot) => {
        this.convention = snapshot.val();
        this.conventionLoaded = true;
        this.loadMembers(this.convention);
      }
    );
  },

  beforeDestroy() {
    if (this.stopAuth) this.stopAuth();
    if (this.stopConnection) this.stopConnection();
    if (this.stopConvention) this.stopConvention();
  },

  methods: {
    async loadMembers(convention) {
      if (!convention) {
        this.members = [];
        return;
      }

      const followers = convention.follower || {};
      const owner = convention.owner || null;
      const uids = Object.keys(followers);
      const token = ++this.membersToken;

      this.loadingMembers = true;

      const rows = await Promise.all(
        uids.map(async (memberUid) => {
          let profile = null;
          try {
            profile = (await get(ref(db, "users/" + memberUid))).val();
          } catch (error) {
            profile = null;
          }
          const role = followers[memberUid];
          return {
            uid: memberUid,
            name: (profile && profile.name) || memberUid,
            username: (profile && profile.username) || "",
            isAdmin: role === "admin" || memberUid === owner,
            isOwner: memberUid === owner,
          };
        })
      );

      if (token !== this.membersToken) return;

      rows.sort((a, b) => {
        if (a.isAdmin !== b.isAdmin) return a.isAdmin ? -1 : 1;
        return a.name.localeCompare(b.name);
      });

      this.members = rows;
      this.loadingMembers = false;
    },

    canManage(member) {
      return this.isAdmin && this.connected && !member.isOwner;
    },

    async setAdmin(member) {
      if (!this.isAdmin || this.saving) return;
      const role = member.isAdmin ? "follower" : "admin";
      this.saving = true;
      try {
        await set(
          ref(db, "users/" + member.uid + "/conventions/" + this.id),
          role
        );
        await set(
          ref(db, "conventions/" + this.id + "/follower/" + member.uid),
          role
        );
        this.notify(
          role === "admin"
            ? member.name + " is now an admin"
            : member.name + " is no longer an admin"
        );
      } catch (error) {
        this.notify(this.errorMessage(error), "error");
      } finally {
        this.saving = false;
      }
    },

    initials(member) {
      const source = member.name || member.uid || "?";
      return source
        .split(" ")
        .filter((part) => part.length > 0)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("");
    },

    errorMessage(error) {
      return (error && error.message) || "Something went wrong";
    },

    notify(text, color) {
      this.snackbar = {
        show: true,
        text: text,
        color: color || "success",
      };
    },
  },
};
</script>
